/**
 * Weather Exception Engine Composable
 * 
 * Provides reactive state and methods for weather exception tracking.
 * Extracted from WeatherExceptionTracker to enable reuse across
 * industry-specific components (Concrete, Shipping, etc.)
 * 
 * Features:
 * - Session/auth integration via vcCommunicator
 * - Multi-location weather data fetching with concurrency control
 * - Criteria-based exception evaluation
 * - In-memory + localStorage caching
 * - Unit conversion (metric/US)
 */

import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { VcDateUtils } from '/src/visualcrossing-core/vcdateutils';
import { getServerRoot, buildTimelineUrl } from './apiConfig.js';

import {
  WEATHER_FIELDS,
  createRuleset,
  createCriterion,
  evaluateRulesetForLocation,
  getActiveAlertsForDay,
  convertRulesetUnits
} from './exceptionCriteriaPresets.js';

import {
  generateCacheKey,
  getCacheEntry,
  setCacheEntry,
  clearAllCache
} from './exceptionCachePersistence.js';

/**
 * Main composable for weather exception tracking
 * @param {Object} options - Configuration options
 * @param {string} options.storageKey - Unique key for localStorage (e.g., 'concrete', 'shipping')
 * @param {string} options.defaultStartPeriod - Default start date period
 * @param {string} options.defaultEndPeriod - Default end date period
 * @param {string} options.defaultUnitGroup - Default unit group ('metric' or 'us')
 * @param {Array} options.defaultCriteria - Default criteria to apply
 * @param {number} options.maxConcurrentRequests - Max parallel API requests
 * @param {string} options.serverRoot - Override server root URL
 */
export function useWeatherExceptionEngine(options = {}) {
  const {
    storageKey = 'default',
    defaultStartPeriod = 'next7days',
    defaultEndPeriod = null,
    defaultUnitGroup = 'metric',
    defaultCriteria = [],
    maxConcurrentRequests = 3,
    serverRoot: serverRootOverride = null
  } = options;

  // ============================================
  // Session/Auth State
  // ============================================
  const sessionId = ref(null);
  const sessionState = ref(null);
  const isContainerInited = ref(false);
  const serverRoot = ref(serverRootOverride);

  const hasSession = computed(() => !!sessionId.value && !!sessionState.value);
  const apiKey = computed(() => sessionState.value?.apiKey || null);
  const userId = computed(() => sessionState.value?.userId || 'anonymous');

  // ============================================
  // Configuration State
  // ============================================
  const unitGroup = ref(defaultUnitGroup);
  const startPeriod = ref(defaultStartPeriod);
  const endPeriod = ref(defaultEndPeriod);

  // ============================================
  // Locations State
  // ============================================
  const locations = ref([]);

  // ============================================
  // Criteria/Ruleset State
  // ============================================
  const activeRuleset = ref(null);

  // ============================================
  // Business Hours (only affects hourly criteria)
  // ============================================
  const businessHoursRef = ref(null); // { enabled, start: 'HH:mm', end: 'HH:mm' }

  // ============================================
  // Run State
  // ============================================
  const isRunning = ref(false);
  const hasRun = ref(false);
  const locationResults = ref([]);
  const requestQueue = ref([]);
  const activeRequests = ref(0);

  // ============================================
  // Computed Properties
  // ============================================

  /**
   * Determine if we need hourly data based on criteria.
   * If any criterion has granularity 'hourly', we need hours data.
   * Also supports legacy 'section' property for backward compat.
   */
  const derivedGranularity = computed(() => {
    if (!activeRuleset.value?.criteria?.length) return 'days';
    
    for (const criterion of activeRuleset.value.criteria) {
      // New granularity property
      if (criterion.granularity === 'hourly') return 'days+hours';
      // Legacy section-based check
      const field = WEATHER_FIELDS[criterion.field];
      if (field?.section === 'hours' || field?.section === 'both') {
        return 'days+hours';
      }
      if (criterion.section === 'hours' || criterion.section === 'both') {
        return 'days+hours';
      }
    }
    return 'days';
  });

  /**
   * Check if we can run the exception check
   */
  const canRun = computed(() => {
    return hasSession.value && 
           locations.value.length > 0 && 
           activeRuleset.value?.criteria?.length > 0;
  });

  /**
   * Progress tracking
   */
  const completedCount = computed(() => {
    return locationResults.value.filter(r => r.status !== 'pending').length;
  });

  const totalToProcess = computed(() => locations.value.length);

  const progressPercent = computed(() => {
    if (totalToProcess.value === 0) return 0;
    return Math.round((completedCount.value / totalToProcess.value) * 100);
  });

  /**
   * Results filtering
   */
  const exceptionCount = computed(() => {
    return locationResults.value.filter(r => r.exceptionCount > 0).length;
  });

  const errorCount = computed(() => {
    return locationResults.value.filter(r => r.status === 'error').length;
  });

  const okCount = computed(() => {
    return locationResults.value.filter(r => r.status === 'ok' && r.exceptionCount === 0).length;
  });

  /**
   * Get unique dates across all location results
   */
  const allDates = computed(() => {
    const dateSet = new Set();
    for (const result of locationResults.value) {
      if (result.days) {
        for (const day of result.days) {
          dateSet.add(day.datetime);
        }
      }
    }
    return Array.from(dateSet).sort();
  });

  // ============================================
  // Date Range Methods
  // ============================================

  /**
   * Resolve date periods to actual ISO date strings
   */
  function resolveDateRange() {
    const [startDate, endDate] = VcDateUtils.resolveDateRange(startPeriod.value, endPeriod.value);
    return {
      startDate: startDate ? VcDateUtils.formatDateIso(startDate) : null,
      endDate: endDate ? VcDateUtils.formatDateIso(endDate) : null
    };
  }

  /**
   * Set date range
   */
  function setDateRange(start, end = null, options = {}) {
    startPeriod.value = start;
    endPeriod.value = end;
    if (!options.skipInvalidation) invalidateResults();
  }

  // ============================================
  // Unit Management
  // ============================================

  /**
   * Change unit group and convert criteria values
   */
  function setUnitGroup(newUnitGroup, options = {}) {
    const oldUnitGroup = unitGroup.value;
    if (oldUnitGroup === newUnitGroup) return;

    if (activeRuleset.value) {
      convertRulesetUnits(activeRuleset.value, oldUnitGroup, newUnitGroup);
    }

    unitGroup.value = newUnitGroup;
    if (!options.skipInvalidation) invalidateResults();
  }

  // ============================================
  // Location Management
  // ============================================

  /**
   * Set locations
   */
  function setLocations(newLocations) {
    locations.value = newLocations;
    invalidateResults();
  }

  /**
   * Add a location
   */
  function addLocation(location, name = null) {
    locations.value.push({ location, name });
    invalidateResults();
  }

  /**
   * Remove a location by index
   */
  function removeLocation(index, options = {}) {
    locations.value.splice(index, 1);
    if (!options.skipInvalidation) invalidateResults();
  }

  /**
   * Clear all locations
   */
  function clearLocations() {
    locations.value = [];
    invalidateResults();
  }

  // ============================================
  // Criteria Management
  // ============================================

  /**
   * Initialize ruleset with criteria
   */
  function initializeRuleset(criteria = [], combineMode = 'OR', name = 'Custom Rules') {
    activeRuleset.value = createRuleset(name, criteria, combineMode);
    invalidateResults();
  }

  /**
   * Build a fingerprint string for a criteria array to detect real changes
   */
  function criteriaFingerprint(criteria) {
    if (!criteria || criteria.length === 0) return '';
    return criteria.map(c => {
      const parts = [c.field, c.operator, c.granularity || 'daily', c.color || ''];
      if (Array.isArray(c.value)) {
        parts.push(c.value.join(','));
      } else {
        parts.push(String(c.value ?? ''));
      }
      if (c.enabled === false) parts.push('off');
      return parts.join('|');
    }).sort().join(';;');
  }

  /**
   * Set criteria directly (replaces existing).
   * Skips invalidation if the criteria definition hasn't actually changed.
   * Re-evaluates existing results when possible instead of forcing re-download.
   */
  function setCriteria(criteria, combineMode = 'OR', options = {}) {
    const oldFp = criteriaFingerprint(activeRuleset.value?.criteria);
    const oldMode = activeRuleset.value?.combineMode || 'OR';

    if (!activeRuleset.value) {
      activeRuleset.value = createRuleset('Custom Rules', [], combineMode);
    }
    activeRuleset.value.criteria = criteria;
    activeRuleset.value.combineMode = combineMode;

    const newFp = criteriaFingerprint(criteria);
    if (newFp === oldFp && combineMode === oldMode) return; // no real change

    if (options.skipInvalidation) return; // caller handles stale tracking

    // If we have fetched data, re-evaluate in place; otherwise full invalidation
    if (hasRun.value && locationResults.value.length > 0) {
      reEvaluateAllResults();
    } else {
      invalidateResults();
    }
  }

  /**
   * Add a criterion
   */
  function addCriterion(field, operator, value, section = 'days', name = null, options = {}) {
    if (!activeRuleset.value) {
      activeRuleset.value = createRuleset('Custom Rules', [], 'OR');
    }
    const criterion = createCriterion(field, operator, value, section, name, options);
    activeRuleset.value.criteria.push(criterion);
    invalidateResults();
    return criterion;
  }

  /**
   * Remove a criterion by index
   */
  function removeCriterion(index) {
    if (activeRuleset.value) {
      activeRuleset.value.criteria.splice(index, 1);
      invalidateResults();
    }
  }

  /**
   * Update business hours reference (used only for hourly criteria filtering).
   * Returns true if the value actually changed.
   */
  function setBusinessHours(bh) {
    const oldBh = businessHoursRef.value;
    const newBh = bh ? { ...bh } : null;
    // Quick equality check
    const same = (!oldBh && !newBh) ||
      (oldBh && newBh &&
        oldBh.enabled === newBh.enabled &&
        oldBh.start === newBh.start &&
        oldBh.end === newBh.end);
    businessHoursRef.value = newBh;
    return !same;
  }

  /**
   * Update a criterion
   */
  function updateCriterion(index, updates) {
    if (activeRuleset.value && activeRuleset.value.criteria[index]) {
      Object.assign(activeRuleset.value.criteria[index], updates);
      invalidateResults();
    }
  }

  /**
   * Set combine mode (AND/OR)
   */
  function setCombineMode(mode) {
    if (activeRuleset.value) {
      activeRuleset.value.combineMode = mode;
      invalidateResults();
    }
  }

  // ============================================
  // Results Management
  // ============================================

  /**
   * Invalidate current results (forces full re-download on next run)
   */
  function invalidateResults() {
    if (hasRun.value) {
      hasRun.value = false;
      locationResults.value = [];
    }
  }

  /**
   * Re-evaluate all existing results with current criteria/business hours
   * without re-fetching weather data from the API.
   */
  function reEvaluateAllResults() {
    for (let i = 0; i < locationResults.value.length; i++) {
      const result = locationResults.value[i];
      if (result.weatherData && result.status === 'ok') {
        processWeatherData(i, result.weatherData, result.cached);
      }
    }
  }

  /**
   * Get result for a specific location
   */
  function getResultForLocation(locationStr) {
    return locationResults.value.find(r => r.location === locationStr);
  }

  /**
   * Get results filtered by status
   */
  function getFilteredResults(filter = 'all') {
    switch (filter) {
      case 'exceptions':
        return locationResults.value.filter(r => r.exceptionCount > 0);
      case 'errors':
        return locationResults.value.filter(r => r.status === 'error');
      case 'ok':
        return locationResults.value.filter(r => r.status === 'ok' && r.exceptionCount === 0);
      default:
        return locationResults.value;
    }
  }

  // ============================================
  // API Request Handling
  // ============================================

  // Promise resolver for runExceptionCheck completion
  let resolveRunComplete = null;

  /**
   * Main function to run the exception check
   * Returns a Promise that resolves when all requests are complete
   */
  async function runExceptionCheck(options = {}) {
    if (!canRun.value) return Promise.resolve();

    const maxLocations = options.maxLocations || 0;

    isRunning.value = true;
    hasRun.value = true;
    locationResults.value = [];
    requestQueue.value = [];
    activeRequests.value = 0;

    const { startDate, endDate } = resolveDateRange();

    // Initialize results with pending status (optionally limited)
    const locsToProcess = maxLocations > 0 ? locations.value.slice(0, maxLocations) : locations.value;
    for (const loc of locsToProcess) {
      const isInvalid = !loc.location || loc.location.trim() === '';
      
      locationResults.value.push({
        id: loc.id || (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36)),
        location: loc.location || '',
        name: loc.name || null,
        status: isInvalid ? 'error' : 'pending',
        errorMessage: isInvalid ? 'Invalid location - empty or missing' : null,
        cached: false,
        nextExceptionDate: null,
        exceptionCount: 0,
        triggeredCriteria: [],
        days: [],
        timezone: null,
        weatherData: null,
        resolvedLocation: null,
        // Severity tracking
        maxSeverity: 'ok', // 'ok', 'warning', 'danger'
        warningCount: 0,
        dangerCount: 0
      });
    }

    // Queue valid locations for fetching
    for (let i = 0; i < locationResults.value.length; i++) {
      const result = locationResults.value[i];
      if (result.status === 'pending') {
        requestQueue.value.push({
          index: i,
          location: result.location,
          name: result.name,
          startDate,
          endDate
        });
      }
    }
    
    // If no pending requests, resolve immediately
    if (requestQueue.value.length === 0) {
      isRunning.value = false;
      return Promise.resolve();
    }

    // Create a promise that resolves when all requests complete
    return new Promise((resolve) => {
      resolveRunComplete = resolve;
      // Start processing queue
      processRequestQueue();
    });
  }

  /**
   * Process the request queue with concurrency limit
   */
  function processRequestQueue() {
    while (activeRequests.value < maxConcurrentRequests && requestQueue.value.length > 0) {
      const request = requestQueue.value.shift();
      activeRequests.value++;
      fetchLocationWeather(request)
        .finally(() => {
          activeRequests.value--;
          processRequestQueue();
        });
    }

    // Check if all done
    if (activeRequests.value === 0 && requestQueue.value.length === 0) {
      isRunning.value = false;
      // Resolve the promise from runExceptionCheck
      if (resolveRunComplete) {
        resolveRunComplete();
        resolveRunComplete = null;
      }
    }
  }

  /**
   * Fetch weather data for a single location
   */
  async function fetchLocationWeather(request) {
    const { index, location, startDate, endDate } = request;
    const result = locationResults.value[index];

    // Check cache first
    const cacheKey = generateCacheKey(
      userId.value,
      location,
      startDate,
      endDate,
      unitGroup.value,
      derivedGranularity.value
    );

    const cached = getCacheEntry(cacheKey);
    if (cached) {
      await new Promise(resolve => setTimeout(resolve, 0));
      processWeatherData(index, cached, true);
      return;
    }

    // Build API URL using centralized config
    const baseUrl = getServerRoot(serverRoot.value);
    const encodedLocation = encodeURIComponent(location);
    let url = `${baseUrl}/VisualCrossingWebServices/rest/services/timeline/${encodedLocation}`;
    
    if (startDate) {
      url += `/${startDate}`;
      if (endDate) {
        url += `/${endDate}`;
      }
    }

    const postData = {
      key: apiKey.value,
      unitGroup: unitGroup.value,
      elements: 'tempmax,tempmin,temp,precip,precipprob,windspeed,windgust,snow,snowdepth,conditions,datetime,humidity,severerisk',
      include: 'days,hours,alerts',
      iconSet: 'icons2'
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        redirect: 'error',
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setCacheEntry(cacheKey, data);
      processWeatherData(index, data, false);

    } catch (error) {
      result.status = 'error';
      result.errorMessage = error.message || 'Failed to fetch weather data';
    }
  }

  /**
   * Process weather data and evaluate exceptions.
   * Produces the same result shape as the server-side WeatherExceptionAnalyzer
   * so that preview and server paths are interchangeable for the UI.
   */
  function processWeatherData(index, data, fromCache) {
    const result = locationResults.value[index];
    
    if (!data || !data.days) {
      result.status = 'error';
      result.errorMessage = 'Invalid response data';
      return;
    }

    result.status = 'ok';
    result.cached = fromCache;
    result.timezone = data.timezone || null;
    
    // Location info — match server LocationExceptionResult shape
    result.latitude = data.latitude;
    result.longitude = data.longitude;
    result.address = data.address || result.location;
    result.resolvedAddress = data.resolvedAddress || data.address || result.location;

    // Backward compat aliases used by ExceptionMapView and other components
    result.resolvedLocation = {
      latitude: data.latitude,
      longitude: data.longitude,
      address: result.resolvedAddress
    };
    result.weatherData = data; // Retained for preview re-evaluation only

    const includeHours = true; // Always evaluate hourly data so hourly view is never blank
    const evaluation = evaluateRulesetForLocation(activeRuleset.value, data, includeHours, businessHoursRef.value);

    result.exceptionCount = evaluation.exceptionCount;
    result.nextExceptionDate = evaluation.nextExceptionDate;
    
    const triggeredSet = new Set();
    evaluation.exceptionDays.forEach(ed => {
      ed.matchedCriteria.forEach(cid => triggeredSet.add(cid));
    });
    result.triggeredCriteria = Array.from(triggeredSet);

    const locationAlerts = Array.isArray(data.alerts) ? data.alerts : [];
    // Store alerts in server-compatible format; alertsData is backward compat alias
    result.alerts = locationAlerts.length > 0 ? locationAlerts : null;
    result.alertsData = result.alerts;

    // Track severity levels
    let maxSeverity = 'ok';
    let warningCount = 0;
    let dangerCount = 0;

    // Pre-index criteria by ID for O(1) lookups
    const criteriaById = {};
    for (const c of (activeRuleset.value?.criteria || [])) {
      criteriaById[c.id] = c;
    }

    result.days = data.days.map(day => {
      const dayEval = evaluation.exceptionDays.find(ed => ed.date === day.datetime);
      const alertInfo = getActiveAlertsForDay(locationAlerts, day.datetime);

      // Determine day severity based on matched criteria colors (same logic as server)
      let daySeverity = 'ok';
      if (dayEval) {
        for (const cid of dayEval.matchedCriteria) {
          const criterion = criteriaById[cid];
          if (criterion?.color === 'danger') {
            daySeverity = 'danger';
            break;
          } else if (criterion?.color === 'warning' || criterion?.color === 'orange') {
            daySeverity = 'warning';
          }
        }
        // Default to warning if has exception but no specific color
        if (daySeverity === 'ok') {
          daySeverity = 'warning';
        }
      }

      if (daySeverity === 'danger') {
        dangerCount++;
        maxSeverity = 'danger';
      } else if (daySeverity === 'warning') {
        warningCount++;
        if (maxSeverity !== 'danger') maxSeverity = 'warning';
      }

      return {
        // Match server DayExceptionResult shape
        datetime: day.datetime,
        isException: !!dayEval,
        severity: daySeverity,
        hasAlerts: alertInfo.hasActiveAlert,
        matchedCriteria: dayEval?.matchedCriteria || [],
        matchedHours: dayEval?.matchedHours || [],
        alertMatchedHours: dayEval?.alertMatchedHours || [],
        hourCriteriaMap: dayEval?.hourCriteriaMap || {},
        // Weather context (same fields as server DayExceptionResult)
        tempmax: day.tempmax,
        tempmin: day.tempmin,
        temp: day.temp,
        feelslike: day.feelslike,
        feelslikemax: day.feelslikemax,
        feelslikemin: day.feelslikemin,
        dew: day.dew,
        precip: day.precip,
        precipprob: day.precipprob,
        precipcover: day.precipcover,
        preciptype: day.preciptype,
        windspeed: day.windspeed,
        windgust: day.windgust,
        snow: day.snow,
        snowdepth: day.snowdepth,
        humidity: day.humidity,
        cloudcover: day.cloudcover,
        visibility: day.visibility,
        pressure: day.pressure,
        severerisk: day.severerisk,
        solarradiation: day.solarradiation,
        solarenergy: day.solarenergy,
        uvindex: day.uvindex,
        sunrise: day.sunrise,
        sunset: day.sunset,
        moonphase: day.moonphase,
        icon: day.icon,
        description: day.description,
        conditions: day.conditions,
        // Hours in server HourExceptionResult-compatible shape
        hours: day.hours || null,
        // UI state (client-only)
        isExpanded: false,
        // Backward compat
        activeAlerts: alertInfo.matchingAlerts
      };
    });

    result.maxSeverity = maxSeverity;
    result.warningCount = warningCount;
    result.dangerCount = dangerCount;
  }

  // ============================================
  // Cache Management
  // ============================================

  function clearCache() {
    clearAllCache();
  }

  // ============================================
  // Session Management
  // ============================================

  function initSession() {
    const vcCommunicator = window.vcCommunicator;
    
    if (!vcCommunicator) {
      console.warn('useWeatherExceptionEngine: vcCommunicator not found');
      return;
    }

    sessionId.value = vcCommunicator.sessionId;
    sessionState.value = vcCommunicator.sessionState;
    isContainerInited.value = vcCommunicator.isInited || sessionId.value;

    vcCommunicator.addHandler('createsession', (sid, state) => {
      sessionId.value = sid;
      sessionState.value = state;
      isContainerInited.value = true;
    });

    vcCommunicator.addHandler('terminatesession', () => {
      sessionId.value = null;
      sessionState.value = null;
      locations.value = [];
      activeRuleset.value = null;
      locationResults.value = [];
      hasRun.value = false;
    });
  }

  // ============================================
  // Formatting Utilities
  // ============================================

  /**
   * Format value for display
   */
  function formatValue(value, type) {
    if (value === null || value === undefined) return '-';

    switch (type) {
      case 'temp':
        return `${Math.round(value)}${unitGroup.value === 'us' ? '°F' : '°C'}`;
      case 'precip':
        return `${Number(value).toFixed(1)}${unitGroup.value === 'us' ? ' in' : ' mm'}`;
      case 'speed':
        return `${Math.round(value)}${unitGroup.value === 'us' ? ' mph' : ' km/h'}`;
      case 'snow':
        return `${Number(value).toFixed(1)}${unitGroup.value === 'us' ? ' in' : ' cm'}`;
      case 'percent':
        return `${Math.round(value)}%`;
      default:
        return value;
    }
  }

  /**
   * Format date for display
   */
  function formatDate(datetime) {
    if (!datetime) return '';
    const parts = datetime.split('-');
    if (parts.length >= 3) {
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      return `${month}/${day}`;
    }
    return datetime;
  }

  /**
   * Get unit label for a field
   */
  function getUnitLabel(fieldId) {
    const field = WEATHER_FIELDS[fieldId];
    if (!field || !field.units) return '';
    return field.units[unitGroup.value] || '';
  }

  // ============================================
  // Return Public API
  // ============================================

  return {
    // Session state
    sessionId,
    sessionState,
    hasSession,
    apiKey,
    userId,
    isContainerInited,
    initSession,

    // Configuration
    unitGroup,
    startPeriod,
    endPeriod,
    serverRoot,
    setUnitGroup,
    setDateRange,
    resolveDateRange,

    // Locations
    locations,
    setLocations,
    addLocation,
    removeLocation,
    clearLocations,

    // Criteria/Ruleset
    activeRuleset,
    derivedGranularity,
    initializeRuleset,
    setCriteria,
    addCriterion,
    removeCriterion,
    updateCriterion,
    setCombineMode,
    setBusinessHours,

    // Run state
    isRunning,
    hasRun,
    canRun,
    locationResults,
    runExceptionCheck,
    invalidateResults,
    reEvaluateAllResults,

    // Results
    completedCount,
    totalToProcess,
    progressPercent,
    exceptionCount,
    errorCount,
    okCount,
    allDates,
    getResultForLocation,
    getFilteredResults,

    // Cache
    clearCache,

    // Formatting
    formatValue,
    formatDate,
    getUnitLabel
  };
}
